import { type CMMBranch, type CMMConversationStep } from '@fruitful/types';

import { DATA_THREAD_METRICS_CHUNK_TYPE } from '../constants/streamDataChunkTypes';
import type { MastraSSEData } from '../types/ChatTypes';
import { isCMMBranch, isCMMStep, isRecord } from './parsingHelpers';

/** Explicit patch written by tools (streamed + merged into thread.metadata.metrics). */
export interface ThreadMetricsPatch {
    branch?: CMMBranch;
    currentStep?: CMMConversationStep;
    collectedModules?: string[];
    /** Semver of the artifact/results computation, published when the artifact is generated. */
    artifactVersion?: string;
}

/** Live surface state from stream / restored message parts (no lastCapturedAt). */
export interface ThreadMetricsSurfaceState {
    branch: CMMBranch | null;
    currentStep: CMMConversationStep | null;
    collectedModules: string[];
}

export const mergeThreadMetricsPatch = (
    existing: ThreadMetricsSurfaceState | null | undefined,
    patch: ThreadMetricsPatch,
): ThreadMetricsSurfaceState => {
    let collectedModules = existing?.collectedModules ?? [];
    if (patch.collectedModules != null) {
        const merged = new Set([
            ...collectedModules,
            ...patch.collectedModules,
        ]);
        collectedModules = [...merged];
    }
    return {
        branch: patch.branch ?? existing?.branch ?? null,
        currentStep: patch.currentStep ?? existing?.currentStep ?? null,
        collectedModules,
    };
};

const parseThreadMetricsPatch = (value: unknown): ThreadMetricsPatch | null => {
    if (!isRecord(value)) {
        return null;
    }

    if (value.type === DATA_THREAD_METRICS_CHUNK_TYPE && isRecord(value.data)) {
        return parseThreadMetricsPatch(value.data);
    }

    const patch: ThreadMetricsPatch = {};
    if (isCMMBranch(value.branch)) {
        patch.branch = value.branch;
    }
    if (isCMMStep(value.currentStep)) {
        patch.currentStep = value.currentStep;
    }
    if (Array.isArray(value.collectedModules)) {
        patch.collectedModules = value.collectedModules.filter(
            (m): m is string => typeof m === 'string',
        );
    }
    if (typeof value.artifactVersion === 'string') {
        patch.artifactVersion = value.artifactVersion;
    }

    if (
        patch.branch == null &&
        patch.currentStep == null &&
        patch.collectedModules == null &&
        patch.artifactVersion == null
    ) {
        return null;
    }

    return patch;
};

/** Extract a metrics patch from a Mastra SSE chunk. */
export const parseThreadMetricsPatchFromSSE = (
    data: MastraSSEData,
): ThreadMetricsPatch | null => {
    return parseThreadMetricsPatch(data.data ?? data);
};
